import React, { useState } from "react";
import { useUserContext } from '../config/UserContext';
import { useKeycloak } from '../config/KeycloakContext';
import AdvancedGrantorModal from "./AdvancedGrantorModal";
import type { Grantor } from "../../../shared/types/models/grantor";

interface AdvancedGrantorCardProps {
  onBack?: () => void;
  onContinue: () => void;
  entityId?: string; // documentLookup
  documentType?: number;
  config?: { showMaritalStatus?: boolean; [key: string]: any };
  readOnly?: boolean;
}

const AdvancedGrantorCard: React.FC<AdvancedGrantorCardProps> = ({ onBack, onContinue, entityId, documentType, config, readOnly }) => {
  const { grantors, refreshGrantors, freeze, apiBaseUrl } = useUserContext();
  const { getToken } = useKeycloak();
  const [modalOpen, setModalOpen] = useState<'individual' | 'trust' | 'married' | 'company' | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group grantors by groupId
  const groups: Record<string, Grantor[]> = {};
  grantors.forEach(g => {
    const gid = g.groupId || g.lookup;
    if (!groups[gid]) groups[gid] = [];
    groups[gid].push(g);
  });

  // Modal save → API call → refresh
  const handleSaveGroup = async (newGroupGrantors: any[]) => {
    if (!freeze?.lookup || !entityId) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/grantors/group`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          grantors: newGroupGrantors,
          documentLookup: entityId,
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to save grantor group");
      }
      await refreshGrantors();
      setModalOpen(null);
      setEditingGroupId(null);
    } catch (err: any) {
      setError(err.message || "Failed to save grantor group");
    } finally {
      setSaving(false);
    }
  };

  // Delete → API call → refresh
  const handleDeleteGroup = async (groupId: string) => {
    if (!freeze?.lookup) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/grantors/group/${groupId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete grantor group");
      await refreshGrantors();
    } catch (err: any) {
      setError(err.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  const handleEditGroup = (groupId: string, type: 'individual' | 'trust' | 'married' | 'company') => {
    setEditingGroupId(groupId);
    setModalOpen(type);
  };

  // Require at least one participating grantor (individual, trustee, or LLC member)
  const hasParticipating = grantors.some(g => g.type !== 'couple' && g.type !== 'trust' && g.type !== 'company' && g.metadata?.claimantType !== 'Non-Participant');

  // Continue just navigates — grantors are already persisted
  const handleContinue = () => {
    if (!readOnly && !hasParticipating) return;
    onContinue();
  };

  return (
    <div className="flex flex-col items-center w-full" style={{ maxWidth: 700, margin: '0 auto' }}>
      
      {/* List of existing groups */}
      <div className="w-full mb-6 flex flex-col gap-4">
        {Object.entries(groups).map(([groupId, groupEntities]) => {
          const hasType = (t: string) => groupEntities.some(e => e.type === t);
          let type: 'individual' | 'trust' | 'married' | 'company' = 'individual';
          if (hasType('trust')) type = 'trust';
          else if (hasType('company') || hasType('member')) type = 'company';
          else if (hasType('couple') || groupEntities.filter(e => e.type === 'individual').length > 1) type = 'married';

          // People to display (filter out containers)
          const people = groupEntities.filter(e => e.type !== 'trust' && e.type !== 'couple' && e.type !== 'company');
          const trustContainer = groupEntities.find(e => e.type === 'trust');
          const companyContainer = groupEntities.find(e => e.type === 'company');

          return (
            <div key={groupId} className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 border border-gray-200">
              <div className="flex justify-between items-center border-b pb-2 mb-2">
                <span className="font-bold text-[#141435]">
                  {type === 'trust' ? (trustContainer?.metadata?.name || 'Trust') + ' Trust'
                   : type === 'company' ? ((companyContainer?.metadata as any)?.name || 'LLC')
                   : type === 'married' ? 'Married Couple'
                   : 'Individual'}
                </span>
                <div className="flex gap-2">
                  {!readOnly && <button onClick={() => handleEditGroup(groupId, type)} className="text-blue-600 hover:text-blue-800 text-sm font-semibold" disabled={saving}>Edit</button>}
                  {!readOnly && <button onClick={() => handleDeleteGroup(groupId)} className="text-red-600 hover:text-red-800 text-sm font-semibold" disabled={saving}>Remove</button>}
                </div>
              </div>
              {people.map((entity, i) => (
                <div key={entity.lookup || i} className="flex flex-col text-sm text-gray-700 ml-2">
                  <span className="font-semibold">
                    {(entity.metadata as any)?.fullName || `${(entity.metadata as any)?.firstName || ''} ${(entity.metadata as any)?.lastName || ''}`.trim()}
                    {(entity.metadata as any)?.role ? ` (${(entity.metadata as any).role})` : ''}
                    {entity.metadata?.phone ? ` | ${entity.metadata.phone}` : ''}
                    {entity.metadata?.email ? ` | ${entity.metadata.email}` : ''}
                  </span>
                  {entity.metadata?.claimantType === 'Non-Participant' && (
                    <span className="text-xs text-red-500 font-bold uppercase tracking-wider mt-1">Non-Participating</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Add Buttons */}
      {!readOnly && (
      <div className="flex flex-row flex-wrap gap-4 mb-8 w-full justify-center">
        <button type="button" className="flex items-center gap-2 bg-[#141435] text-white font-plus-jakarta font-bold text-[14px] py-2 px-6 rounded-full hover:bg-[#23235a] transition-colors" onClick={() => setModalOpen('individual')} disabled={saving}>
          Add Individual
        </button>
        <button type="button" className="flex items-center gap-2 bg-[#141435] text-white font-plus-jakarta font-bold text-[14px] py-2 px-6 rounded-full hover:bg-[#23235a] transition-colors" onClick={() => setModalOpen('married')} disabled={saving}>
          Add Married Couple
        </button>
        <button type="button" className="flex items-center gap-2 bg-[#485D7D] text-white font-plus-jakarta font-bold text-[14px] py-2 px-6 rounded-full hover:bg-[#23235a] transition-colors" onClick={() => setModalOpen('trust')} disabled={saving}>
          Add Trust
        </button>
        <button type="button" className="flex items-center gap-2 bg-[#485D7D] text-white font-plus-jakarta font-bold text-[14px] py-2 px-6 rounded-full hover:bg-[#23235a] transition-colors" onClick={() => setModalOpen('company')} disabled={saving}>
          Add LLC
        </button>
      </div>
      )}

      {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
      
      {!readOnly && !hasParticipating && grantors.length > 0 && (
         <div className="text-red-500 text-sm text-center mb-4 font-semibold">
            You must have at least one participating grantor to continue.
         </div>
      )}

      {!readOnly && (
      <div className="flex flex-row w-full max-w-[700px] gap-4 mt-4 mx-auto">
        <button type="button" className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[10px] px-5 rounded-full hover:bg-[#485D7D] transition-colors" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className={`flex-1 bg-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-[10px] px-5 rounded-full transition-colors ${!hasParticipating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#23235a]'}`}
          onClick={handleContinue}
          disabled={!hasParticipating}
        >
          Continue
        </button>
      </div>
      )}

      {modalOpen && (
        <AdvancedGrantorModal
          open={!!modalOpen}
          type={modalOpen}
          onClose={() => { setModalOpen(null); setEditingGroupId(null); }}
          onSave={handleSaveGroup}
          existingEntities={editingGroupId ? groups[editingGroupId] : undefined}
          config={config}
        />
      )}
    </div>
  );
};

export default AdvancedGrantorCard;
